import { useMemo, useState, type InputHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import {
  PASSWORD_RULES,
  scorePassword,
  type StrengthLevel,
} from '@/lib/passwordStrength'

const levelColor: Record<StrengthLevel, string> = {
  empty: 'bg-terminal-elevated',
  weak: 'bg-danger',
  fair: 'bg-accent/70',
  good: 'bg-accent',
  strong: 'bg-bid',
}

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  showStrength?: boolean
  label?: string
}

/**
 * Password field with live strength meter + regex rule checklist.
 */
export function PasswordInput({
  value,
  onChange,
  showStrength = true,
  label,
  className,
  id,
  ...rest
}: PasswordInputProps) {
  const { t } = useTranslation('common')
  const [visible, setVisible] = useState(false)
  const strength = useMemo(() => scorePassword(value), [value])

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={id} className="text-xs font-medium text-terminal-muted">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <input
          {...rest}
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={rest.autoComplete ?? 'new-password'}
          className={cn(
            'h-9 w-full rounded border border-terminal-border bg-terminal-bg pe-10 ps-2.5 text-sm',
            'text-terminal-fg placeholder:text-terminal-muted',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 end-0 flex w-10 items-center justify-center text-terminal-muted hover:text-terminal-fg"
          aria-label={visible ? t('password.hide') : t('password.show')}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      {showStrength ? (
        <div className="space-y-1.5">
          <div className="flex h-1 overflow-hidden rounded bg-terminal-elevated">
            <div
              className={cn(
                'h-full transition-all duration-300',
                levelColor[strength.level],
              )}
              style={{ width: `${strength.score}%` }}
            />
          </div>
          <p className="text-[11px] text-terminal-muted">
            {t(`password.strength.${strength.level}`)}
          </p>
          <ul className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
            {PASSWORD_RULES.map((rule) => {
              const ok = strength.passed[rule.id]
              return (
                <li
                  key={rule.id}
                  className={cn(
                    'flex items-center gap-1.5 text-[11px]',
                    ok ? 'text-bid' : 'text-terminal-muted',
                  )}
                >
                  <span aria-hidden>{ok ? '✓' : '○'}</span>
                  {t(rule.labelKey)}
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M3 3l14 14M8.5 8.6A2.5 2.5 0 0 0 11.4 11.5M6.2 6.4C4.2 7.5 2.5 10 2.5 10S5.5 15.5 10 15.5c1.3 0 2.5-.4 3.5-1M9.2 5.6c.3 0 .5-.1.8-.1C14.5 5.5 17.5 10 17.5 10c-.3.5-.7 1.1-1.2 1.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
